import {refreshLinkClickTracking} from "@snowplow/browser-plugin-link-click-tracking";
import {enableActivityTrackingCallback, enableActivityTracking, trackSelfDescribingEvent} from "@snowplow/browser-tracker";
import {Logger} from "./Logger";

export function EngagementTracking(debugEnabled, maxChecks) {
  let logger = Logger(debugEnabled);
  const maxNrChecks = maxChecks || 10;
  const checkTimeout = 1000;

  function setDebug(debug) {
    logger = Logger(debug);
  }

  function setupLinkClickTrackingRefresh(nrChecks, nrLinks) {
    const nrLinksNow = document.querySelectorAll('a').length;
    if (nrChecks >= maxNrChecks) {
      logger.debug('engagementTracking: refreshLinkClickTracking, stopping after nrChecks', nrChecks, 'total links found', nrLinksNow);
    } else if (typeof nrLinks === 'undefined' || nrLinks === nrLinksNow) {
      window.setTimeout(setupLinkClickTrackingRefresh, checkTimeout, nrChecks + 1, nrLinksNow);
    } else {
      logger.debug('engagementTracking: refreshLinkClickTracking, new links found', nrLinks, nrLinksNow);
      refreshLinkClickTracking();
      window.setTimeout(setupLinkClickTrackingRefresh, checkTimeout, nrChecks + 1, nrLinksNow);
    }
  }

  function trackButton(el) {
    const classList = el.classList;
    const classArray = new Array(classList.length);
    for (let step = 0; step < classList.length; step++) {
      classArray[step] = classList.item(step);
    }
    logger.debug("engagementTracking: trackButtonClick", el.id, classArray.toString(), el.innerText);
    trackSelfDescribingEvent({
      event: {
        schema: 'iglu:be.investdata.engagement/button_click/jsonschema/1-0-0',
        data: {
          elementId: el.id,
          elementClasses: classArray,
          elementContent: el.innerText
        }
      }
    });
  }

  function determineButtonAndTrack(el) {
    if (el.tagName === 'BUTTON') {
      trackButton(el);
    } else if (el.parentElement) {
      return determineButtonAndTrack(el.parentElement);
    }
  }

  function buttonClickTrackFunction(event) {
    logger.debug("engagementTracking: button clicked", event);
    determineButtonAndTrack(event.target);
  }

  function setupButtonClickTrackingWithRefresh(nrChecks, nrButtons) {
    const buttonNodeList = document.querySelectorAll('button');
    const nrButtonsNow = buttonNodeList.length;
    if (nrChecks >= maxNrChecks) {
      logger.debug('engagementTracking: refreshButtonClickTracking, stopping after nrChecks', nrChecks);
    } else if (typeof nrButtons === 'undefined' || nrButtons === nrButtonsNow) {
      window.setTimeout(setupButtonClickTrackingWithRefresh, checkTimeout, nrChecks + 1, nrButtonsNow);
    } else {
      logger.debug('engagementTracking: refreshButtonClickTracking, new buttons found', nrButtons, nrButtonsNow);
      buttonNodeList.forEach(button => {
        button.addEventListener('click', buttonClickTrackFunction);
      });
      window.setTimeout(setupButtonClickTrackingWithRefresh, checkTimeout, nrChecks + 1, nrButtonsNow);
    }
  }

  function setupPagePingAggregates() {
    const pagePingInterval = 1;
    const start = Date.now();
    let lastPingEvent = start;
    let aggregatedEvent = {
      pageViewId: null,
      minXOffset: 0,
      maxXOffset: 0,
      minYOffset: 0,
      maxYOffset: 0,
      numEvents: 0
    };
    let timeoutID;
    let eventNotSent = true;

    function sendPagePingAggregate() {
      if (eventNotSent) {
        eventNotSent = false;
        let timeOnPageSeconds = Math.round((lastPingEvent - start) / 1000);
        let activeSeconds = aggregatedEvent.numEvents * pagePingInterval;
        const aggregatedPingEvent = {
          event: {
            schema: 'iglu:be.investdata.engagement/page_ping_aggregate/jsonschema/1-1-0',
            data: {
              minXOffset: Math.max(0, Math.round(aggregatedEvent.minXOffset)),
              maxXOffset: Math.max(0, Math.round(aggregatedEvent.maxXOffset)),
              minYOffset: Math.max(0, Math.round(aggregatedEvent.minYOffset)),
              maxYOffset: Math.max(0, Math.round(aggregatedEvent.maxYOffset)),
              activeSeconds: activeSeconds,
              timeOnPageSeconds: timeOnPageSeconds
            }
          }
        };
        logger.debug('engagementTracking: sending ping event', aggregatedPingEvent);
        trackSelfDescribingEvent(aggregatedPingEvent);
      }
    }

    enableActivityTrackingCallback({
      minimumVisitLength: pagePingInterval,
      heartbeatDelay: pagePingInterval,
      callback: (event) => {
        logger.debug('engagementTracking: received ping event', event);
        lastPingEvent = Date.now();
        if (timeoutID) clearTimeout(timeoutID);
        timeoutID = setTimeout(sendPagePingAggregate, 5 * 60 * 1000); //if no signal for 5 minutes: flush
        aggregatedEvent = {
          pageViewId: event.pageViewId,
          minXOffset: aggregatedEvent.minXOffset < event.minXOffset ? aggregatedEvent.minXOffset : event.minXOffset,
          maxXOffset: aggregatedEvent.maxXOffset > event.maxXOffset ? aggregatedEvent.maxXOffset : event.maxXOffset,
          minYOffset: aggregatedEvent.minYOffset < event.minYOffset ? aggregatedEvent.minYOffset : event.minYOffset,
          maxYOffset: aggregatedEvent.maxYOffset > event.maxYOffset ? aggregatedEvent.maxYOffset : event.maxYOffset,
          numEvents: aggregatedEvent.numEvents + 1
        };
      }
    });
    window.addEventListener('unload', sendPagePingAggregate);
  }

  function setupPagePings() {
    enableActivityTracking({minimumVisitLength: 5, heartbeatDelay: 5});
  }

  return {
    setDebug: setDebug,
    setupLinkClickTrackingRefresh: setupLinkClickTrackingRefresh,
    setupButtonClickTrackingWithRefresh: setupButtonClickTrackingWithRefresh,
    setupPagePingAggregates: setupPagePingAggregates,
    setupPagePings: setupPagePings
  }
}
